import { Camera, ArrowUpRight } from "lucide-react";

const SHOTS = [
  {
    src: "https://customer-assets.emergentagent.com/job_solar-install-pro-9/artifacts/pxproekp_Panels%20and%20GI%20look.jpg",
    title: "Tiled-roof villa array",
    caption:
      "Full-black mono panels on GI structures across a multi-pitch terracotta roof — Karnataka countryside.",
    tag: "Residential · Panels & GI",
    span: "lg:col-span-2 lg:row-span-2",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solar-install-pro-9/artifacts/u8wx9tp4_Solis%20Inverter.jpg",
    title: "Solis inverter + DCDB / ACDB",
    caption:
      "On-grid Solis inverter flanked by sealed DC/AC distribution boxes — clean cable management.",
    tag: "Electrical · Inverter Bay",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solar-install-pro-9/artifacts/2z5pdckt_ACDB%20and%20DCDB%20Conduit%20look.jpg",
    title: "ACDB with conduit run",
    caption:
      "Transparent AC distribution box with phase indicators, paired with branded inverter — surge & lightning protected.",
    tag: "Safety · ACDB",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solar-install-pro-9/artifacts/rnicytlb_Cooping%20with%20Water%20Proofing%20Look.jpg",
    title: "Elevated structure + waterproofing",
    caption:
      "Mid-rise galvanised columns on coped, waterproofed pedestals — terrace stays usable below the array.",
    tag: "Civil · Mid-Rise Structure",
    span: "lg:col-span-2",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_solar-install-pro-9/artifacts/75g20jem_I.jfif",
    title: "Underside cable routing",
    caption:
      "Module-level optimisers and protected DC lines under the array — engineered for monsoon-grade reliability.",
    tag: "Detail · Cabling",
  },
];

export default function RealInstallations() {
  return (
    <section
      id="installations-gallery"
      data-testid="real-installs-section"
      className="section-pad relative"
    >
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#FFC857] mb-6">
              — Real Installations
            </div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-light leading-[1.1] max-w-3xl">
              From{" "}
              <span className="italic text-[#FFC857]">our own rooftops</span>{" "}
              — not stock photos.
            </h2>
          </div>
          <div className="flex items-center gap-3 text-[#A0ABC0] max-w-md">
            <Camera className="h-5 w-5 text-[#FFC857] shrink-0" />
            <p className="text-sm md:text-base font-light leading-relaxed">
              Every system on this page was designed, built and commissioned by
              the Jashvi Energy in-house team.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 auto-rows-[260px] md:auto-rows-[320px] gap-5">
          {SHOTS.map((shot, i) => (
            <figure
              data-testid={`real-shot-${i}`}
              key={shot.title}
              className={`group relative overflow-hidden rounded-sm border border-white/10 bg-[#0A1128] ${
                shot.span || ""
              }`}
            >
              <img
                src={shot.src}
                alt={shot.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-[#050B14]/30 to-transparent opacity-90 group-hover:opacity-100 transition-opacity" />

              <div className="absolute top-4 left-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#050B14]/70 backdrop-blur-md border border-white/10">
                <span className="h-1.5 w-1.5 rounded-full bg-[#FFC857]" />
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#FFC857]">
                  {shot.tag}
                </span>
              </div>

              <figcaption className="absolute inset-x-0 bottom-0 p-6 md:p-7">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-serif text-xl md:text-2xl text-white leading-tight">
                      {shot.title}
                    </h3>
                    <p className="mt-2 text-xs md:text-sm text-[#A0ABC0] font-light leading-relaxed max-w-md opacity-90">
                      {shot.caption}
                    </p>
                  </div>
                  <ArrowUpRight className="h-5 w-5 text-[#FFC857] shrink-0 mt-1 transition-transform duration-500 group-hover:rotate-45" />
                </div>
              </figcaption>
            </figure>
          ))}
        </div>

        <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-8 border-t border-white/10">
          <p className="text-sm text-[#A0ABC0] font-light">
            160+ live systems across Karnataka — each one engineered, not
            outsourced.
          </p>
          <button
            data-testid="real-installs-cta"
            onClick={() =>
              document
                .getElementById("enquiry")
                ?.scrollIntoView({ behavior: "smooth" })
            }
            className="btn-outline-gold"
          >
            Plan your installation <ArrowUpRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
